import { useMemo, useCallback } from "react"
import { chain } from "lodash"
import { usePositionSetMetadata } from "resources/position-sets"
import { combineStatuses, combineTanStackStatuses, DataStatus, OK, ResourceResult } from "helpers/data-statuses"
import { computeFilteredSegment } from "helpers/segments"
import { VIEW_TYPES } from "helpers/base"
import { useTagCategories } from "resources/tags"
import { useAttributeCategories } from "resources/attributes"
import { useClassifications } from "resources/classifications"
import { getAllAvailableRollups } from "helpers/rollups"
import { IsoDate } from "helpers/types/dates"
import { SelectedInstrument } from "helpers/types/instruments/selected"
import { InstrumentMetadata, InstrumentWithMetadata } from "helpers/types/instruments/metadata"

// [TODO:TS] Put somewhere else
interface ModelMetadata {
  availability: {
    securitiesStartDate: IsoDate
    currentDate: IsoDate
  }
}

// [TODO:TS] Put somewhere else
export type Normalization = "STANDALONE" | "CONTRIBUTION"

// [TODO:TS] Implement type
type Filter = object

export const INSTRUMENTS = {
  rebalanced: {
    metricsSuffix: "-rebalanced",
    suffix: "Rebalanced",
  },
  original: {
    metricsSuffix: "",
    suffix: "",
  },
  comparison: {
    metricsSuffix: "-comparison",
    suffix: "Comparison",
  },
  end: {
    metricsSuffix: "",
    suffix: "",
  },
  start: {
    metricsSuffix: "-comparison",
    suffix: "Comparison",
  },
  target: {
    metricsSuffix: "-target",
    suffix: "Target",
  },
  delta: {
    metricsSuffix: "-delta",
    suffix: "Delta",
    hasDataGetter: true,
  },
} as const

export const INSTRUMENT_TYPES = {
  original: "original",
  rebalanced: "rebalanced",
  comparison: "comparison",
  delta: "delta",
  target: "target",
  start: "start",
  end: "end",
} as const
export type InstrumentTypes = (typeof INSTRUMENT_TYPES)[keyof typeof INSTRUMENT_TYPES]

export const SELECTED_MODE_VALUES = {
  original: INSTRUMENT_TYPES.original,
  rebalanced: INSTRUMENT_TYPES.rebalanced,
  comparison: INSTRUMENT_TYPES.comparison,
  start: INSTRUMENT_TYPES.start,
  end: INSTRUMENT_TYPES.end,
  rebalancedVsOriginal: "rebalancedVsOriginal",
  rebalancedVsComparison: "rebalancedVsComparison",
  originalVsComparison: "originalVsComparison",
  endVsStart: "endVsStart",
  rebalancedVsEnd: "rebalancedVsEnd",
  rebalancedVsStart: "rebalancedVsStart",
} as const
export type SelectedMode = (typeof SELECTED_MODE_VALUES)[keyof typeof SELECTED_MODE_VALUES]

export const REBALANCED_MODES = [
  SELECTED_MODE_VALUES.rebalanced,
  SELECTED_MODE_VALUES.rebalancedVsOriginal,
  SELECTED_MODE_VALUES.rebalancedVsComparison,
] as const

export const COMPARISON_MODES = [
  SELECTED_MODE_VALUES.originalVsComparison,
  SELECTED_MODE_VALUES.rebalancedVsComparison,
  SELECTED_MODE_VALUES.comparison,
] as const

export const END_VS_START_MODES = [
  SELECTED_MODE_VALUES.endVsStart,
  SELECTED_MODE_VALUES.rebalancedVsEnd,
  SELECTED_MODE_VALUES.rebalancedVsStart,
] as const

export const INSTRUMENTS_BY_MODE: Record<SelectedMode, InstrumentTypes[]> = {
  [SELECTED_MODE_VALUES.original]: [INSTRUMENT_TYPES.original],
  [SELECTED_MODE_VALUES.rebalanced]: [INSTRUMENT_TYPES.rebalanced],
  [SELECTED_MODE_VALUES.comparison]: [INSTRUMENT_TYPES.comparison],
  [SELECTED_MODE_VALUES.start]: [INSTRUMENT_TYPES.comparison],
  [SELECTED_MODE_VALUES.end]: [INSTRUMENT_TYPES.original],
  [SELECTED_MODE_VALUES.rebalancedVsOriginal]: [INSTRUMENT_TYPES.rebalanced, INSTRUMENT_TYPES.original, INSTRUMENT_TYPES.delta],
  [SELECTED_MODE_VALUES.rebalancedVsComparison]: [INSTRUMENT_TYPES.rebalanced, INSTRUMENT_TYPES.comparison, INSTRUMENT_TYPES.delta],
  [SELECTED_MODE_VALUES.originalVsComparison]: [INSTRUMENT_TYPES.original, INSTRUMENT_TYPES.comparison, INSTRUMENT_TYPES.delta],
  [SELECTED_MODE_VALUES.endVsStart]: [INSTRUMENT_TYPES.original, INSTRUMENT_TYPES.comparison, INSTRUMENT_TYPES.delta],
  [SELECTED_MODE_VALUES.rebalancedVsEnd]: [INSTRUMENT_TYPES.rebalanced, INSTRUMENT_TYPES.original, INSTRUMENT_TYPES.delta],
  [SELECTED_MODE_VALUES.rebalancedVsStart]: [INSTRUMENT_TYPES.rebalanced, INSTRUMENT_TYPES.comparison, INSTRUMENT_TYPES.delta],
}

interface ModeSelectorOption {
  id: SelectedMode
  primaryText: string
  group: number
  needComparison?: boolean
  needRebalanced?: boolean
}

export const MODE_SELECTOR_OPTIONS: Record<SelectedMode, ModeSelectorOption> = {
  [SELECTED_MODE_VALUES.rebalancedVsOriginal]: { id: SELECTED_MODE_VALUES.rebalancedVsOriginal, primaryText: "Rebalanced [R] vs Original [O]", needRebalanced: true, group: 0 },
  [SELECTED_MODE_VALUES.rebalancedVsComparison]: { id: SELECTED_MODE_VALUES.rebalancedVsComparison, primaryText: "Rebalanced [R] vs Comparison [C]", needRebalanced: true, needComparison: true, group: 0 },
  [SELECTED_MODE_VALUES.originalVsComparison]: { id: SELECTED_MODE_VALUES.originalVsComparison, primaryText: "Original [O] vs Comparison [C]", needComparison: true, group: 0 },
  [SELECTED_MODE_VALUES.rebalancedVsEnd]: { id: SELECTED_MODE_VALUES.rebalancedVsEnd, primaryText: "Rebalanced [R] vs End [E]", needRebalanced: true, group: 0 },
  [SELECTED_MODE_VALUES.rebalancedVsStart]: { id: SELECTED_MODE_VALUES.rebalancedVsStart, primaryText: "Rebalanced [R] vs Start [S]", needRebalanced: true, group: 0 },
  [SELECTED_MODE_VALUES.endVsStart]: { id: SELECTED_MODE_VALUES.endVsStart, primaryText: "End [E] vs Start [S]", needComparison: true, group: 0 },
  [SELECTED_MODE_VALUES.rebalanced]: { id: SELECTED_MODE_VALUES.rebalanced, primaryText: "Rebalanced [R]", needRebalanced: true, group: 1 },
  [SELECTED_MODE_VALUES.original]: { id: SELECTED_MODE_VALUES.original, primaryText: "Original [O]", group: 1 },
  [SELECTED_MODE_VALUES.comparison]: { id: SELECTED_MODE_VALUES.comparison, primaryText: "Comparison [C]", needComparison: true, group: 1 },
  [SELECTED_MODE_VALUES.end]: { id: SELECTED_MODE_VALUES.end, primaryText: "End [E]", group: 1 },
  [SELECTED_MODE_VALUES.start]: { id: SELECTED_MODE_VALUES.start, primaryText: "Start [S]", needComparison: true, group: 1 },
}

export const DEFAULT_MODE_SELECTOR_OPTIONS = [
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.rebalancedVsOriginal],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.rebalancedVsComparison],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.originalVsComparison],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.rebalanced],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.original],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.comparison],
]

export const END_VS_START_MODE_SELECTOR_OPTIONS = [
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.rebalancedVsEnd],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.rebalancedVsStart],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.endVsStart],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.rebalanced],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.end],
  MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.start],
]

export function getInstrumentOrder(mode: SelectedMode): InstrumentTypes[] {
  switch (mode) {
    case SELECTED_MODE_VALUES.rebalancedVsComparison:
      return ["rebalanced", "comparison"]
    case SELECTED_MODE_VALUES.originalVsComparison:
      return ["original", "comparison"]
    case SELECTED_MODE_VALUES.rebalancedVsOriginal:
      return ["rebalanced", "original"]
    case SELECTED_MODE_VALUES.rebalanced:
      return ["rebalanced"]
    case SELECTED_MODE_VALUES.comparison:
      return ["comparison"]
    default:
      return ["original"]
  }
}

// Order of columns:
// | Original   | Rebalanced or Comparison | Delta |
// | Rebalanced | Comparison               | Delta |
export function useModeInstruments(instrument: InstrumentTypes, secondInstrument: InstrumentTypes, targetInstrument: InstrumentTypes, mode: SelectedMode, shouldSkipDelta: boolean): InstrumentTypes[] {
  return useMemo(() => {
    switch (mode) {
      case SELECTED_MODE_VALUES.rebalancedVsComparison:
      case SELECTED_MODE_VALUES.rebalancedVsStart:
        return shouldSkipDelta ? [targetInstrument] : [secondInstrument, targetInstrument]
      case SELECTED_MODE_VALUES.originalVsComparison:
        return [instrument, targetInstrument]
      case SELECTED_MODE_VALUES.endVsStart:
        return shouldSkipDelta ? [instrument] : [instrument, targetInstrument]
      case SELECTED_MODE_VALUES.rebalancedVsOriginal:
      case SELECTED_MODE_VALUES.rebalancedVsEnd:
        return shouldSkipDelta ? [instrument] : [secondInstrument, instrument]
      case SELECTED_MODE_VALUES.rebalanced:
        return [secondInstrument]
      case SELECTED_MODE_VALUES.comparison:
      case SELECTED_MODE_VALUES.start:
        return [targetInstrument]
      default:
      case SELECTED_MODE_VALUES.original:
      case SELECTED_MODE_VALUES.end:
        return [instrument]
    }
  }, [instrument, secondInstrument, targetInstrument, mode, shouldSkipDelta])
}

export function useInstrumentsWithMetadata(modelId: string, currentDate: IsoDate, instruments: Record<string, SelectedInstrument>): [Record<string, InstrumentWithMetadata>, DataStatus] {
  // [TODO:TS] Type those!
  const [originalMetadata, originalMetadataStatus] = usePositionSetMetadata(modelId, instruments.original, currentDate) as ResourceResult<InstrumentMetadata>
  const [comparisonMetadata, comparisonMetadataStatus] = usePositionSetMetadata(modelId, instruments.comparison, currentDate) as ResourceResult<InstrumentMetadata>
  const [baseMetadata, baseMetadataStatus] = usePositionSetMetadata(modelId, instruments.base, currentDate) as ResourceResult<InstrumentMetadata>
  const status = combineStatuses([originalMetadataStatus, comparisonMetadataStatus, baseMetadataStatus])

  const instrumentsWithMetadata = useMemo(
    (): Record<string, InstrumentWithMetadata> => ({
      original: { ...instruments.original, ...originalMetadata },
      ...(instruments.comparison && { comparison: { ...instruments.comparison, ...comparisonMetadata } }),
      ...(instruments.rebalanced && { rebalanced: { ...instruments.rebalanced, ...originalMetadata, name: `${originalMetadata?.name} (Rebalanced)` } }),
      ...(instruments.base && { base: { ...instruments.base, ...baseMetadata } }),
    // [TODO:TS] Fix the as
    } as Record<string, InstrumentWithMetadata>),
    [instruments.original, instruments.comparison, instruments.rebalanced, instruments.base, originalMetadata, comparisonMetadata, baseMetadata])

  return [instrumentsWithMetadata, status]
}

export function useInstrumentsEarliestAndLatestDates(modelMetadata: ModelMetadata, instrumentsWithMetadata: Record<string, InstrumentWithMetadata>) {
  return useMemo((): [IsoDate, IsoDate] => {
    const earliestDate = chain(instrumentsWithMetadata)
      .pick(["original", "comparison", "base"])
      .map(instrument => instrument.earliestDate)
      .concat(modelMetadata.availability.securitiesStartDate)
      .max()
      .value()

    const latestDate = chain(instrumentsWithMetadata)
      .pick(["original", "comparison", "base"])
      .map(instrument => instrument.latestDate)
      .concat(modelMetadata.availability.currentDate)
      .min()
      .value()

    return [earliestDate, latestDate]
  }, [instrumentsWithMetadata, modelMetadata])
}

type AugmentedInstrumentWithMedata = InstrumentWithMetadata & { segment: object | null, expand: boolean }

export function useInstrumentsWithQueryOptions(instruments: Record<string, InstrumentWithMetadata>, selectedMode: SelectedMode, { filter, normalization, isExpanded }: {
  filter?: Filter
  normalization?: Normalization
  isExpanded: boolean
}) {
  const segment: object | null = useMemo(() =>
    computeFilteredSegment(filter, normalization),
  [normalization, filter])

  const makeAugmentedInstrument = useCallback((instrument: InstrumentWithMetadata): AugmentedInstrumentWithMedata => ({
    ...instrument,
    segment,
    expand: isExpanded,
  }), [segment, isExpanded])

  // If we rebalance an instrument down to 0, we still want the instrument to appear in the table, so we query the original instrument so the position data is not null
  const instrumentTypesToUse = useMemo(() => {
    const instrumentTypes = INSTRUMENTS_BY_MODE[selectedMode] || []
    if (instrumentTypes.includes(INSTRUMENT_TYPES.rebalanced) && !instrumentTypes.includes(INSTRUMENT_TYPES.original)) {
      return [...instrumentTypes, INSTRUMENT_TYPES.original]
    }
    return instrumentTypes
  }, [selectedMode])

  return useMemo(() => {
    return instrumentTypesToUse.reduce((acc, instrumentType) => {
      if (!instruments[instrumentType]) {
        return acc
      }
      acc[instrumentType] = makeAugmentedInstrument(instruments[instrumentType])
      return acc
    }, {} as Record<InstrumentTypes, AugmentedInstrumentWithMedata>)
  }, [instruments, instrumentTypesToUse, makeAugmentedInstrument])
}

// [TODO:TS]
export function useBaseWithSegment(base: InstrumentWithMetadata | null, filter?: Filter, normalization?: Normalization): InstrumentWithMetadata | null {
  return useMemo(() => {
    if (!base) {
      return null
    }

    const segment = computeFilteredSegment(filter, normalization)

    if (!segment) {
      return base
    }

    return {
      ...base,
      segment,
    }
  }, [base, filter, normalization])
}

export function useAvailableGroupByOptions(instrument: InstrumentWithMetadata, date: IsoDate, { isActive = false, hasMultipleUniqueInstruments = false }): [unknown[], DataStatus] {
  const viewType = isActive ? VIEW_TYPES.ACTIVE : VIEW_TYPES.ABSOLUTE
  const [tagCategories, tagCategoriesStatus] = useTagCategories(instrument)
  const [attributeCategories, attributeCategoriesStatus] = useAttributeCategories(instrument)
  const [classifications, classificationsStatus] = useClassifications()

  const status = combineTanStackStatuses([tagCategoriesStatus, attributeCategoriesStatus, classificationsStatus])

  const accountRollups = useMemo(() => ({
    classifications,
    tagCategories: tagCategories,
    attributeCategories: attributeCategories,
  }), [tagCategories, attributeCategories, classifications])

  const rollups = useMemo(() => {
    if (status !== OK) {
      return []
    }
    // [TODO:TS] Fix when rollups get typed
    return getAllAvailableRollups(accountRollups as never, date, viewType, hasMultipleUniqueInstruments)
  }, [accountRollups, date, viewType, status, hasMultipleUniqueInstruments])

  return [rollups, status]
}

export function getOrderedInstrumentColumns(mode: SelectedMode): InstrumentTypes[] {
  const instrumentColumns = INSTRUMENTS_BY_MODE[mode]
  if (!instrumentColumns) {
    return ["original"]
  }
  return instrumentColumns
}
